import { useState } from "react";

import { getFrameworkLogoDomain } from "@/data/frameworkLogos";
import { cn } from "@/lib/utils";

const TOKEN = import.meta.env.VITE_LOVABLE_CONNECTOR_LOGO_DEV_API_KEY as
  | string
  | undefined;

interface FrameworkLogoProps {
  /** Framework or regulation name, e.g. "ISO 27001". */
  name: string;
  /** Rendered box size in px. Defaults to 16. */
  size?: number;
  /**
   * Render the issuer's mark in its own colours. Off by default: at 12-16px,
   * in a dense rail, a row of full-colour marks reads as noise. Turn it on
   * where the logo is large enough to be identified — the pack tiles.
   */
  color?: boolean;
  className?: string;
}

/**
 * Logo of the body that issues a framework or regulation, pulled from Logo.dev.
 * Renders nothing when there is no mapped issuer, no publishable key, or the
 * request fails, so callers can always keep their text label as the fallback.
 */
const FrameworkLogo = ({ name, size = 16, color = false, className }: FrameworkLogoProps) => {
  const [failed, setFailed] = useState(false);
  const domain = getFrameworkLogoDomain(name);

  if (!domain || !TOKEN) return null;

  const src = `https://img.logo.dev/${domain}?token=${TOKEN}&size=${size * 2}&format=png&retina=true&fallback=404`;

  // The wrapper always reserves the same box so a slow or failed fetch never
  // shifts the surrounding layout.
  return (
    <span
      data-testid="framework-logo"
      aria-hidden
      className={cn("inline-block shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {!failed && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          width={size}
          height={size}
          onError={() => setFailed(true)}
          className={cn("h-full w-full object-contain", !color && "grayscale opacity-80")}
        />
      )}
    </span>
  );
};

export default FrameworkLogo;
